// HTML редактора -> ограниченный HTML, который принимает Telegram (parse_mode=HTML)

const INLINE_TAGS: Record<string, string> = {
    B: "b",
    STRONG: "b",
    I: "i",
    EM: "i",
    U: "u",
    INS: "u",
    S: "s",
    DEL: "s",
    STRIKE: "s",
    CODE: "code",
    PRE: "pre",
    BLOCKQUOTE: "blockquote",
};

const BLOCK_TAGS = new Set([
    "P",
    "DIV",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "LI",
    "PRE",
    "BLOCKQUOTE",
]);

const escapeText = (text: string): string =>
    text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const serialize = (node: Node): string => {
    if (node.nodeType === 3) {
        return escapeText(node.textContent || "");
    }

    if (node.nodeType !== 1) {
        return "";
    }

    const el = node as HTMLElement;
    const tag = el.tagName;

    if (tag === "BR") {
        return "\n";
    }

    const inner = Array.from(el.childNodes).map(serialize).join("");

    if (tag === "A") {
        const href = el.getAttribute("href") || "";
        // только безопасные схемы, иначе оставляем текст без ссылки
        return /^(https?:\/\/|tg:\/\/|mailto:)/i.test(href)
            ? `<a href="${escapeText(href).replace(/"/g, "&quot;")}">${inner}</a>`
            : inner;
    }

    const mapped = INLINE_TAGS[tag];
    const body = mapped ? `<${mapped}>${inner}</${mapped}>` : inner;

    if (tag === "LI") {
        const parent = el.parentElement;
        const marker =
            parent && parent.tagName === "OL"
                ? `${Array.from(parent.children).indexOf(el) + 1}. `
                : "• ";
        return `${marker}${body}\n`;
    }

    return BLOCK_TAGS.has(tag) ? `${body}\n` : body;
};

export const toTelegramHtml = (html: string): string => {
    const doc = new DOMParser().parseFromString(html || "", "text/html");

    return serialize(doc.body)
        .replace(/\n{3,}/g, "\n\n")
        .trim();
};

export const telegramHtmlToText = (html: string): string =>
    toTelegramHtml(html)
        .replace(/<[^>]*>/g, "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .trim();

/** Bot API: лимит после разбора entities, UTF-16 (как JS string.length) */
export const TG_TEXT_LIMIT = 4096;
export const TG_CAPTION_LIMIT = 1024;
export const TG_ENTITY_LIMIT = 100;
export const TG_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const TG_VIDEO_MAX_BYTES = 50 * 1024 * 1024;

export const telegramLimitStats = (editorHtml: string, hasMedia: boolean) => {
    const chars = telegramHtmlToText(editorHtml).length;
    const entities = (
        toTelegramHtml(editorHtml).match(
            /<(?:b|i|u|s|code|pre|a|blockquote)\b/gi
        ) || []
    ).length;
    const limit = hasMedia ? TG_CAPTION_LIMIT : TG_TEXT_LIMIT;

    return {
        chars,
        limit,
        entities,
        remaining: limit - chars,
        overChars: chars > limit,
        overEntities: entities > TG_ENTITY_LIMIT,
    };
};

export const telegramHtmlToPreviewHtml = (html: string): string =>
    toTelegramHtml(html).replace(/\n/g, "<br>");

export const telegramHtmlToEditorHtml = (html: string): string => {
    if (!html.trim()) {
        return "";
    }
    if (/<\/p>/i.test(html)) {
        return html;
    }

    return html
        .split("\n")
        .map((line) => `<p>${line || "<br>"}</p>`)
        .join("");
};
